import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Clock, BookOpen, Flame } from 'lucide-react';
import { CircularProgress } from './CircularProgress';
import { CourseImageCard } from '@/components/course/CourseImageCard';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
interface ContinueLearningHeroProps {
  courseTitle: string;
  courseCategory?: string;
  moduleTitle?: string;
  progress: number;
  completedModules: number;
  totalModules: number;
  lastAccessedAt?: Date;
  onContinue: () => void;
  className?: string;
}

export const ContinueLearningHero: React.FC<ContinueLearningHeroProps> = ({
  courseTitle,
  courseCategory,
  moduleTitle,
  progress,
  completedModules,
  totalModules,
  lastAccessedAt,
  onContinue,
  className,
}) => {
  return (
    <Card className={cn(
      'relative overflow-hidden border-primary/20',
      className
    )}>
      {/* Background Image */}
      <div className="absolute inset-0 opacity-30">
        <CourseImageCard
          category={courseCategory}
          className="w-full h-full"
          showCategoryLabel={false}
        />
      </div>
      
      {/* Gradient overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/80" />
      
      <CardContent className="relative p-6 md:p-8">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          {/* Progress Ring */}
          <div className="flex-shrink-0">
            <CircularProgress value={progress} size="lg" strokeWidth={8} />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Flame className="h-5 w-5 text-orange-500" />
              <span className="text-sm font-medium text-muted-foreground">Continue Where You Left Off</span>
            </div>
            
            <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2 truncate">
              {courseTitle}
            </h2>
            
            {courseCategory && (
              <Badge variant="secondary" className="mb-3">
                {courseCategory}
              </Badge>
            )}
            
            {moduleTitle && (
              <p className="text-muted-foreground mb-3 flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                <span className="truncate">Currently on: {moduleTitle}</span>
              </p>
            )}
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <BookOpen className="h-4 w-4" />
                {completedModules} / {totalModules} modules completed
              </span>
              {lastAccessedAt && (
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {formatDistanceToNow(lastAccessedAt, { addSuffix: true })}
                </span>
              )}
            </div>
          </div>
          
          {/* CTA Button */}
          <div className="flex-shrink-0 w-full md:w-auto">
            <Button
              onClick={onContinue}
              size="lg"
              className="w-full md:w-auto gap-2 shadow-lg hover:shadow-xl transition-all"
            >
              <Play className="h-5 w-5" />
              Continue Learning
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContinueLearningHero;
